/**
 * shared/decisions.ts — the Decisions primitive (parent card VcBi3nr9GjBj,
 * child A JLkBEaxDx6As).
 *
 * A decision is a couple-authored two-option choice. Each partner picks in
 * private, and the same card reveals both picks together. The SAME shape is
 * served to both partners; only the seal differs (see projectDecision).
 *
 * Seal rule (AC2 of the parent card): while a decision is `pending`, the
 * response carries WHO has voted and never WHAT they picked — no choices, no
 * counts, no per-option tallies. Your own pick is echoed back to you so the
 * UI can show "Locked in" on the option you chose; the partner's is absent
 * entirely, not blanked in the client. The projection below is the ONLY place
 * that decides this, and it runs server-side before the row leaves the room.
 *
 * Options are always visible: a person cannot vote on text they cannot read.
 * The sealed thing is the vote, not the question.
 */

export const DECISION_STATES = ['pending', 'revealed', 'tied'] as const;
export type DecisionState = (typeof DECISION_STATES)[number];

export const DECISION_CHOICES = ['a', 'b'] as const;
export type DecisionChoice = (typeof DECISION_CHOICES)[number];

/** Card verbs, locked 2026-10-20. Never "Vote", never "Poll". */
export const DECISION_VERBS: Readonly<Record<DecisionChoice, string>> = {
  a: 'Lock in',
  b: 'Either works',
};

/** One nudge inside the push nuisance budget, then "still undecided" at 48h. */
export const DECISION_STALL_MS = 48 * 60 * 60 * 1000;
export const DECISION_NUDGE_MS = 24 * 60 * 60 * 1000;

/**
 * What the 48h rule does to one pending decision at a given clock (card
 * bwcTJh8Od5rk). Pure, so the sweep (which WRITES), the report (which only
 * reads) and a probe all answer the same question from one definition:
 *
 *   none  — too young, or already nudged inside the nudge window
 *   nudge — past the nudge window, no nudge spent yet: spend it once
 *   stall — past 48h: file the agenda talking point, never close the decision
 *
 * The stall is idempotent by construction (nudged once, so `alreadyNudged`
 * keeps returning 'none' for the nudge half) — the agenda marker table is what
 * makes the FILE half safe to repeat.
 */
export type DecisionStallAction = 'none' | 'nudge' | 'stall';

export function stallAction(input: { createdAt: number; nudgedAt: number | null; now: number }): DecisionStallAction {
  const age = input.now - input.createdAt;
  if (age >= DECISION_STALL_MS) return 'stall';
  if (age >= DECISION_NUDGE_MS && !input.nudgedAt) return 'nudge';
  return 'none';
}

/** How a decision came to exist. Recorded on every creation (AC6). */
export type DecisionInitiationSource = 'typed_message' | 'composer' | 'date_needs_time' | 'request_stall' | 'inbox_proposal';

export interface DecisionVoteView {
  memberId: string;
  /** Present only once the decision is revealed/tied, or on your own vote while pending. */
  choice?: DecisionChoice;
  mine: boolean;
  votedAt: number;
}

export interface DecisionView {
  decisionId: string;
  /** The generic messages.round_id this card renders through — never a new message kind. */
  roundId: string;
  authorId: string;
  title: string;
  options: { a: string; b: string };
  state: DecisionState;
  createdAt: number;
  resolvedAt: number | null;
  /** pending and past the 48h stall window: still openable, nudging stopped. */
  stalled: boolean;
  initiationSource: DecisionInitiationSource;
  votes: DecisionVoteView[];
  /** Set once resolved: both picks, or the tie line. Null while pending. */
  outcomeLine: string | null;
  /** Agent-proposed options beyond the couple's two (Plus only). Empty for the
   *  free two-option card, which is the launch shape. */
  extraOptions: string[];
}

export interface DecisionRecord {
  decision_id: string;
  round_id: string;
  author_id: string;
  title: string;
  option_a: string;
  option_b: string;
  initiation_source: string;
  outcome_text: string | null;
  state: string;
  created_at: number;
  resolved_at: number | null;
  nudged_at: number | null;
}

export interface DecisionVoteRow {
  decision_id: string;
  member_id: string;
  choice: string;
  voted_at: number;
}

function normalizeState(raw: unknown): DecisionState {
  const v = String(raw ?? '');
  return (DECISION_STATES as readonly string[]).includes(v) ? (v as DecisionState) : 'pending';
}

function normalizeSource(raw: unknown): DecisionInitiationSource {
  const v = String(raw ?? '');
  return v === 'composer' || v === 'date_needs_time' || v === 'request_stall' || v === 'inbox_proposal'
    ? (v as DecisionInitiationSource)
    : 'typed_message';
}

/**
 * The seal. Pure so the room and any test can share one definition.
 * `viewerId` is the member asking; the other member's choice is stripped
 * while the decision is pending.
 */
export function projectDecision(
  rec: DecisionRecord,
  votes: DecisionVoteRow[],
  viewerId: string,
  now: number = Date.now(),
  extraOptions: string[] = [],
): DecisionView {
  const state = normalizeState(rec.state);
  const resolved = state !== 'pending';
  const sealed: DecisionVoteView[] = votes.map(v => {
    const mine = v.member_id === viewerId;
    const base: DecisionVoteView = { memberId: v.member_id, mine, votedAt: v.voted_at };
    if (resolved || mine) base.choice = v.choice === 'b' ? 'b' : 'a';
    return base;
  });

  let outcomeLine: string | null = null;
  if (state === 'revealed') outcomeLine = rec.outcome_text ?? 'Both of you picked.';
  else if (state === 'tied') outcomeLine = rec.outcome_text ?? 'You picked differently — it is on the Plan agenda.';

  return {
    decisionId: rec.decision_id,
    roundId: rec.round_id,
    authorId: rec.author_id,
    title: rec.title,
    options: { a: rec.option_a, b: rec.option_b },
    state,
    createdAt: rec.created_at,
    resolvedAt: rec.resolved_at ?? null,
    stalled: state === 'pending' && now - rec.created_at >= DECISION_STALL_MS,
    initiationSource: normalizeSource(rec.initiation_source),
    votes: sealed,
    outcomeLine,
    extraOptions: extraOptions.filter(e => typeof e === 'string' && e.trim() !== ''),
  };
}

/**
 * Typed-message arming (child B, card HMzNgJETH7HE).
 *
 * The composer stays nine tiles: the trigger for a decision is the shape of
 * what a person already typed, not a menu (parent card VcBi3nr9GjBj, LOCKED
 * 2026-10-20). This is that shape, deterministic and shared so the chip the
 * Thread offers and the title/options the server stores can never disagree:
 *
 *   two-option  "Pizza or pasta?" / "Should we fly or drive?"  -> matches
 *   favour      "Can you pick up milk?"                        -> null (Requests)
 *   plain       "Dinner at 7"                                  -> null
 *
 * Two options only. A sentence that splits on a second "or" has three, so it
 * returns null rather than arming a card with a silently dropped option.
 */
export interface DecisionIntent {
  title: string;
  optionA: string;
  optionB: string;
}

/** Favour shape is Requests' trigger (card o4i7UUbHTn5M), never a decision. */
const FAVOUR_SHAPE = /^(can|could|would|will|shall|may)\s+you\b/i;

/** Question stems folded into the title, not into the first option. */
const STEM_SHAPE = /^(should we|shall we|do we|don't we|are we|is it|can we|could we|would we|will we)\s+/i;

const TRAILING_PUNCT = /[.?!,;:]+$/;

export function parseDecisionIntent(raw: string): DecisionIntent | null {
  const text = raw.trim().replace(/\s+/g, ' ');
  if (!text || text.length > 200) return null;
  if (FAVOUR_SHAPE.test(text)) return null;

  const split = text.match(/^(.+?)\s+or\s+(.+)$/i);
  if (!split) return null;
  const left = split[1].trim();
  const right = split[2].trim();
  /* Three or more options is not a decision at launch — it is a dropped one. */
  if (/\s+or\s+/i.test(right)) return null;

  const optionA = left.replace(STEM_SHAPE, '').trim().replace(TRAILING_PUNCT, '');
  const optionB = right.replace(TRAILING_PUNCT, '');
  if (!optionA || !optionB) return null;
  if (optionA.length > 60 || optionB.length > 60) return null;
  if (optionA.toLowerCase() === optionB.toLowerCase()) return null;

  const title = text.replace(TRAILING_PUNCT, '');
  if (!title) return null;
  return { title, optionA, optionB };
}

/**
 * Resolve state from the two picks. Both voted -> tied when they differ,
 * revealed when they agree. Returns null while only one (or neither) has
 * voted, so "still pending" is a value, not an exception.
 */
export function settleDecision(votes: { choice: string }[]): { state: 'revealed' | 'tied'; outcome: string } | null {
  if (votes.length < 2) return null;
  const picks = votes.slice(0, 2).map(v => (v.choice === 'b' ? 'b' : 'a'));
  if (picks[0] === picks[1]) {
    return { state: 'revealed', outcome: `You both picked ${picks[0] === 'a' ? 'the first' : 'the second'} option.` };
  }
  return { state: 'tied', outcome: 'You picked differently — it is on the Plan agenda.' };
}

/* ── Couple-level surfaces (child C, card bwcTJh8Od5rk) ───────────────────
 * Everything below is pure so the Life rail, the worker and a probe read ONE
 * definition of "what the rail shows" and "what the meeting is told". */

/**
 * The line a tied or stalled decision files onto the weekly meeting's agenda.
 * It carries the QUESTION, never a pick: the agenda is a talking point for the
 * couple, so neither partner's choice is on it (that stays in the sealed
 * decision, which both can still open).
 */
export function decisionAgendaText(view: Pick<DecisionView, 'title' | 'state'>): string {
  return view.state === 'tied'
    ? `Decisions — you picked differently on “${view.title}”`
    : `Decisions — “${view.title}” is still undecided after 48 hours`;
}

export interface DecisionRailSelection {
  /** Open decisions, newest first. */
  open: DecisionView[];
  /** The ONE open decision the Life rail may draw, or null. */
  visible: DecisionView | null;
  /** Every open decision — this is what the Thread rail's count includes. */
  openCount: number;
  /** Settled decisions (revealed / tied), newest first. */
  resolved: DecisionView[];
}

/** At most one open decision on the Life rail; the rest is a count, not a
 *  stack (parent card AC7). Resolved rows never take a rail slot. */
export function selectDecisionRail(views: DecisionView[]): DecisionRailSelection {
  const sorted = [...views].sort((a, b) => b.createdAt - a.createdAt);
  const open = sorted.filter(v => v.state === 'pending');
  const resolved = sorted.filter(v => v.state !== 'pending');
  return { open, visible: open[0] ?? null, openCount: open.length, resolved };
}

/**
 * The needs_time route's two candidate times (parent AC6): the next date slot
 * becomes the first option, the one after it the second. Both are prefilled —
 * a person picks a time, never types one. Local time, because "Thursday
 * evening" is the couple's evening, not the worker's UTC.
 */
export function dateTimeCandidates(now: number = Date.now()): { a: string; b: string } {
  const DAY = 86_400_000;
  const label = (t: number) =>
    new Date(t).toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' });
  const d = new Date(now);
  d.setHours(18, 30, 0, 0);
  /* Next Thursday 6:30pm — the preferred slot (shared/date-nudge.ts). */
  while (d.getDay() !== 4 || d.getTime() <= now) d.setTime(d.getTime() + DAY);
  const a = d.getTime();
  /* Then the next Saturday 11:00 — the fallback slot, a real second choice. */
  d.setDate(d.getDate() + 1);
  d.setHours(11, 0, 0, 0);
  while (d.getDay() !== 6) d.setTime(d.getTime() + DAY);
  return { a: label(a), b: label(d.getTime()) };
}

/** Two options are free forever; a third and beyond is an agent-proposed extra
 *  and sits behind Plus (parent card, locked rule-free / agent-Plus pattern).
 *  Server-side only — a client cannot buy itself a gate by sending one. */
export const DECISION_FREE_OPTION_LIMIT = 2;
